import { useRouter } from 'next/router'

import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'

import { DEFAULT_CURRENCY } from '@/lib/constants'
import { Device } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import { useDeviceName } from '@/hooks/useDeviceName'

import { DeviceStatus } from '@/components/DeviceStatus'

export const DevicePerformance = ({
  device,
  angleComponent,
  className,
  isDialogCard,
  isMainPerformanceHidden
}: {
  device: Device
  angleComponent?: JSX.Element
  className?: string
  isMainPerformanceHidden?: boolean
  isDialogCard?: boolean
}) => {
  const { locale } = useRouter()
  const { formatMessage } = useIntl()
  const { deviceName } = useDeviceName({ device })

  const {
    miner,
    currency: deviceCurrency,
    status,
    pricePerTH,
    nextUpdateAt
  } = device
  const { power, preorderFor } = miner

  const currency = deviceCurrency || DEFAULT_CURRENCY

  return (
    <>
      <div
        className={clsx(
          'flex max-tablet:flex-col max-phone:flex-wrap',
          !isMainPerformanceHidden && 'mb-3 gap-1 tablet:gap-3'
        )}
      >
        <div className="max-phone:contents">
          <h3 className="text-x-base font-medium leading-x-base tablet:pb-1 tablet:text-xl tablet:leading-xl">
            {deviceName}
          </h3>
          <div
            className={clsx(
              'flex items-center gap-2',
              isDialogCard && preorderFor && 'max-phone:flex-col'
            )}
          >
            <DeviceStatus
              nextUpdateAt={nextUpdateAt}
              preorderFor={preorderFor}
              status={status}
            />
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'relative text-x-sm leading-x-sm text-secondary-500',
                  'tablet:text-x-base tablet:leading-x-base',
                  'pl-2 after:absolute after:left-0 after:top-[45%] after:h-0.5',
                  'after:w-0.5 after:rounded-full after:bg-secondary-500'
                )}
              >
                {miner.hashrate} TH/
                {formatMessage({ id: 'unit.sec', defaultMessage: 'сек' })}
              </span>
              <span
                className={clsx(
                  'relative pl-2 text-x-sm leading-x-sm text-secondary-500 after:absolute after:left-0 after:top-[45%]',
                  'after:h-0.5 after:w-0.5 after:rounded-full after:bg-secondary-500 tablet:text-x-base tablet:leading-x-base'
                )}
              >
                {power}{' '}
                {formatMessage({
                  id: `unit.power`,
                  defaultMessage: 'кВт'
                })}
              </span>
            </div>
          </div>
        </div>
        {angleComponent}
      </div>
      {!isMainPerformanceHidden && !isDialogCard && (
        <div
          className={clsx(
            'flex gap-2 desktop:w-fit desktop:min-w-[500px]',
            className
          )}
        >
          <div className="flex flex-auto flex-col gap-1 rounded-md bg-slate-50 p-2">
            <span
              className={clsx(
                'text-[11px] leading-[1.27] tracking-[-0.24px] tablet:text-x-sm tablet:leading-x-sm'
              )}
            >
              <FormattedMessage
                {...{ id: 'device.price', defaultMessage: 'Цена' }}
              />{' '}
              <span>1TH</span>
            </span>
            <span
              className={clsx(
                'text-x-base font-medium leading-x-base tablet:text-x-md tablet:leading-x-md'
              )}
            >
              {!!pricePerTH && formatCurrency(pricePerTH, currency, locale)}
            </span>
          </div>
          <div className="flex flex-auto flex-col gap-1 rounded-md bg-slate-50 p-2">
            <span
              className={clsx(
                'text-[11px] leading-[1.27] tracking-[-0.24px] tablet:text-x-sm tablet:leading-x-sm'
              )}
            >
              <FormattedMessage
                {...{
                  id: 'device.efficiency',
                  defaultMessage: 'Эффективность'
                }}
              />
            </span>
            <span className="text-x-base font-medium leading-x-base tablet:text-x-md tablet:leading-x-md">
              {((miner.power / miner.hashrate) * 1000).toFixed(2)}
              {formatMessage({
                id: `unit.efficiency`,
                defaultMessage: 'Вт/TH'
              })}
            </span>
          </div>
          <div className="flex flex-auto flex-col gap-1 rounded-md bg-slate-50 p-2">
            <span
              className={clsx(
                'text-[11px] leading-[1.27] tracking-[-0.24px] tablet:text-x-sm tablet:leading-x-sm'
              )}
            >
              <FormattedMessage
                {...{ id: 'device.price', defaultMessage: 'Цена' }}
              />{' '}
              <span>
                1
                {formatMessage({
                  id: `unit.power`,
                  defaultMessage: 'кВт'
                })}
              </span>
            </span>
            <span className="text-x-base font-medium leading-x-base tablet:text-x-md tablet:leading-x-md">
              {formatCurrency(miner.location.pricePerKwh, currency, locale)}
            </span>
          </div>
        </div>
      )}
    </>
  )
}
